// ============================================================================
// UI Breakdown — Progress bar + numeric display per skor komponen
// Ref: Google Docs 'Metodologi Skor Kepercayaan' Section 4.2
// ============================================================================
// Operator hover/click kandidat tile → popover muncul dgn breakdown lengkap:
//   Biometrik ██████████ 38.0 / 40
//   Nama      ████████░░ 22.0 / 25
//   Handle    ███████░░░ 11.0 / 15
//   Lokasi    █████░░░░░  5.0 / 10
//   Multi-plat ██████░░░░  6.0 / 10
//   Bonus NIK            +15
//   ──────────────────────────
//   Total: 94 [maks: 100]
//   ✓ AUTO
// ============================================================================

import React from 'react';

const ProgressBar = ({ value, max, color = 'var(--color-cyan)' }) => {
  const percentage = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{
      flex: 1, height: 6, background: 'var(--tint)',
      borderRadius: 3, overflow: 'hidden',
    }}>
      <div style={{
        width: `${percentage}%`, height: '100%',
        background: color, transition: 'width 0.3s ease',
      }} />
    </div>
  );
};

const ScoreRow = ({ label, value, max, color }) => (
  <div className="flex items-center gap-2 mb-1.5" style={{ fontSize: 9, fontFamily: 'IBM Plex Mono' }}>
    <span style={{ flex: '0 0 70px', color: 'var(--text-mid)' }}>{label}</span>
    <ProgressBar value={value} max={max} color={color} />
    <span style={{ flex: '0 0 50px', textAlign: 'right', color: 'var(--text-hi)', fontWeight: 700 }}>
      {value.toFixed(1)} / {max}
    </span>
  </div>
);

/**
 * Render score breakdown per candidate per Google Docs Section 4.2.
 *
 * @param {{score, score_cap, breakdown, bonuses, auto_confirm, alias_detected, alias_floor_applied, data_elevated, elevated_types, biometric_gate_warning}} composite
 */
export function CandidateScoreBreakdown({ composite }) {
  if (!composite) return null;
  const {
    score, score_cap, breakdown, bonuses,
    auto_confirm, alias_detected, alias_floor_applied, biometric_floor_applied,
    data_elevated, elevated_types,
    biometric_gate_warning,
  } = composite;

  const totalBonus = (bonuses?.nik || 0) + (bonuses?.keywords || 0) + (bonuses?.employer || 0) + (bonuses?.location || 0);

  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-cyan)',
      borderRadius: 8, padding: 12, width: 280,
      boxShadow: '0 4px 24px rgba(0,0,0,0.6), 0 0 12px var(--tint)',
    }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2"
        style={{ borderBottom: '1px solid var(--border-dim)' }}>
        <span style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 900,
          color: 'var(--color-cyan)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
          Score Breakdown
        </span>
        <span style={{ fontSize: 14, fontFamily: 'IBM Plex Mono', fontWeight: 900,
          color: 'var(--color-cyan)' }}>{score}</span>
      </div>

      {/* Biometric Gate warning kalau biometrik unavailable */}
      {biometric_gate_warning && (
        <div className="mb-2 px-2 py-1 rounded" style={{
          background: 'rgba(255, 214, 10, 0.08)', border: '1px solid rgba(255, 214, 10, 0.35)',
          fontSize: 8, fontFamily: 'IBM Plex Mono', color: 'var(--color-yellow)',
        }}>
          ⚠ {biometric_gate_warning}
        </div>
      )}

      {/* BASE SCORE rows — 5 components */}
      <div className="mb-2">
        <ScoreRow label="Biometrik" value={breakdown.biometric} max={40} />
        <ScoreRow label="Nama"      value={breakdown.name}      max={25} />
        <ScoreRow label="Handle"    value={breakdown.handle}    max={15} />
        <ScoreRow label="Lokasi"    value={breakdown.location}  max={10} />
        <ScoreRow label="Multi-plat" value={breakdown.cross}    max={10} />
      </div>

      {/* BONUS section kalau ada */}
      {totalBonus > 0 && (
        <div className="mb-2 pt-2" style={{ borderTop: '1px dashed var(--border-dim)' }}>
          <p style={{ fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 700,
            color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
            Bonus Additive
          </p>
          {bonuses.nik > 0 && (
            <div style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--color-green)' }}>
              + Bonus NIK <span style={{ float: 'right', fontWeight: 900 }}>+{bonuses.nik}</span>
            </div>
          )}
          {bonuses.keywords > 0 && (
            <div style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--color-green)' }}>
              + Bonus Keywords <span style={{ float: 'right', fontWeight: 900 }}>+{bonuses.keywords}</span>
            </div>
          )}
          {bonuses.employer > 0 && (
            <div style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--color-green)' }}>
              + Bonus Tempat Kerja <span style={{ float: 'right', fontWeight: 900 }}>+{bonuses.employer}</span>
            </div>
          )}
          {bonuses.location > 0 && (
            <div style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--color-green)' }}>
              + Bonus Lokasi Terkini <span style={{ float: 'right', fontWeight: 900 }}>+{bonuses.location}</span>
            </div>
          )}
        </div>
      )}

      {/* BIOMETRIC FLOOR transparency — addendum v2 2026-05-19 (kasus
          borneosonofficial 63% MATCH). Lebih luas dari ALIAS floor v1. */}
      {biometric_floor_applied && (
        <div className="mb-2 pt-2 px-2 py-1 rounded" style={{
          borderTop: '1px dashed var(--border-dim)',
          background: biometric_floor_applied.label === 'strong'
            ? 'rgba(0, 212, 255, 0.08)' : 'rgba(255, 214, 10, 0.08)',
          border: biometric_floor_applied.label === 'strong'
            ? '1px solid rgba(0, 212, 255, 0.35)' : '1px solid rgba(255, 214, 10, 0.35)',
          fontSize: 8, fontFamily: 'IBM Plex Mono',
          color: biometric_floor_applied.label === 'strong'
            ? 'var(--color-cyan)' : 'var(--color-yellow)',
        }}>
          ⚠ BIOMETRIC FLOOR ({biometric_floor_applied.label.toUpperCase()}) diterapkan:
          wajah cocok ≥{Math.round(biometric_floor_applied.threshold * 100)}% → skor di-floor ke {biometric_floor_applied.floor}.
          {alias_detected ? ' Plus ALIAS detected (nama beda jauh).' : ''} Operator wajib verifikasi.
        </div>
      )}

      {/* TOTAL */}
      <div className="pt-2" style={{ borderTop: '1px solid var(--border-mid)' }}>
        <div className="flex items-center justify-between" style={{
          fontSize: 10, fontFamily: 'IBM Plex Mono', fontWeight: 900,
        }}>
          <span style={{ color: 'var(--text-hi)' }}>Total</span>
          <span style={{ color: 'var(--color-cyan)' }}>
            {score} <span style={{ color: 'var(--text-mute)', fontWeight: 400 }}>[maks: {score_cap}]</span>
          </span>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1 mt-2">
          {auto_confirm && (
            <span style={{
              fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
              background: 'var(--color-green)', color: 'var(--color-bg)',
              padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5,
            }}>✓ AUTO</span>
          )}
          {alias_detected && (
            <span style={{
              fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
              background: 'var(--color-orange)', color: 'var(--color-bg)',
              padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5,
            }} title="Wajah sama tapi nama berbeda — kemungkinan akun alias">USERNAME UNIK</span>
          )}
          {data_elevated && elevated_types.length > 0 && (
            <span style={{
              fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
              background: 'var(--color-purple)', color: 'var(--color-bg)',
              padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5,
            }} title={`Data sensitif: ${elevated_types.join(', ')}`}>
              🔒 {elevated_types.map(t => t.toUpperCase()).join(' ')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
